import type { TranscriptionToken } from '../transcription/types';

/**
 * Internal representation of a registered token with its DOM element
 */
interface RegisteredToken {
  token: TranscriptionToken;
  element: HTMLElement;
}

/**
 * Non-React class that manages token highlighting via direct DOM manipulation.
 * Subscribes to audio player time updates and updates CSS attributes on token elements.
 *
 * Key performance characteristics:
 * - Binary search for O(log n) token lookup
 * - Direct DOM attribute updates (no React rerenders)
 * - Only updates when active token changes
 * - Can be safely swapped or disabled without breaking React
 *
 * Example:
 * ```typescript
 * const controller = new TokenHighlightController();
 * tokens.forEach((token, i) => {
 *   const el = tokenElements[i];
 *   controller.registerToken(token, el);
 * });
 *
 * audio.onTimeUpdate = (timeMs) => {
 *   controller.onTimeUpdate(timeMs);
 * };
 * ```
 */
export class TokenHighlightController {
  /** Tokens sorted by startMs */
  private tokens: RegisteredToken[] = [];

  /** Index of currently highlighted token (-1 if none) */
  private currentHighlightedIndex = -1;

  /** Whether highlighting is enabled */
  private isEnabled = true;

  /**
   * Register a token's DOM element for highlighting.
   * Tokens are kept in chronological order by startMs.
   */
  registerToken(token: TranscriptionToken, element: HTMLElement): void {
    // Find insertion point (keep tokens sorted by startMs)
    const insertIndex = this.tokens.findIndex(
      (t) => t.token.startMs > token.startMs,
    );

    const registered: RegisteredToken = { token, element };

    if (insertIndex === -1) {
      // Insert at end
      this.tokens.push(registered);
    } else {
      // Insert at correct position
      this.tokens.splice(insertIndex, 0, registered);
    }
  }

  /**
   * Unregister a token when its DOM element is removed.
   */
  unregisterToken(element: HTMLElement): void {
    const index = this.tokens.findIndex((t) => t.element === element);
    if (index !== -1) {
      // Remove highlight from element before removing (avoids "stuck" highlight on layout change)
      if (index === this.currentHighlightedIndex) {
        element.removeAttribute('data-highlighted');
        this.currentHighlightedIndex = -1;
      } else if (index < this.currentHighlightedIndex) {
        this.currentHighlightedIndex -= 1;
      }
      this.tokens.splice(index, 1);
    }
  }

  /**
   * Clear all registered tokens and remove highlighting.
   */
  clearAll(): void {
    // Remove highlight from current token
    if (
      this.currentHighlightedIndex >= 0 &&
      this.currentHighlightedIndex < this.tokens.length
    ) {
      const element = this.tokens[this.currentHighlightedIndex].element;
      element.removeAttribute('data-highlighted');
    }

    this.tokens = [];
    this.currentHighlightedIndex = -1;
  }

  /**
   * Called by audio player on each time update.
   * Finds the active token at currentTimeMs and updates DOM highlighting.
   *
   * @param currentTimeMs Current playback time in milliseconds
   */
  onTimeUpdate(currentTimeMs: number): void {
    if (!this.isEnabled || this.tokens.length === 0) return;

    // Binary search for the token that contains currentTimeMs
    const activeIndex = this.findActiveTokenIndex(currentTimeMs);

    // No change, skip DOM updates
    if (activeIndex === this.currentHighlightedIndex) return;

    // Remove previous highlight
    if (
      this.currentHighlightedIndex >= 0 &&
      this.currentHighlightedIndex < this.tokens.length
    ) {
      const prevElement = this.tokens[this.currentHighlightedIndex].element;
      prevElement.removeAttribute('data-highlighted');
    }

    // Add new highlight
    if (activeIndex >= 0 && activeIndex < this.tokens.length) {
      const activeElement = this.tokens[activeIndex].element;
      activeElement.setAttribute('data-highlighted', 'true');
    }

    this.currentHighlightedIndex = activeIndex;
  }

  /**
   * Binary search to find the token containing currentTimeMs.
   * Returns the index of the token where startMs <= currentTimeMs <= endMs.
   * Returns -1 if no token contains the current time.
   */
  private findActiveTokenIndex(currentTimeMs: number): number {
    let left = 0;
    let right = this.tokens.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const token = this.tokens[mid].token;

      // Check if currentTimeMs falls within this token
      if (currentTimeMs >= token.startMs && currentTimeMs <= token.endMs) {
        return mid;
      }

      // Adjust search range
      if (currentTimeMs < token.startMs) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    // No exact match - return closest preceding token (for boundary cases)
    return left > 0 ? left - 1 : -1;
  }

  /**
   * Enable/disable highlighting without clearing tokens.
   * Useful for pausing highlighting when user manually scrolls.
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Check if highlighting is currently enabled.
   */
  isActiveEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Get current state (for debugging/monitoring).
   */
  getDebugState() {
    return {
      tokenCount: this.tokens.length,
      currentHighlightedIndex: this.currentHighlightedIndex,
      isEnabled: this.isEnabled,
      currentHighlightedToken:
        this.currentHighlightedIndex >= 0
          ? this.tokens[this.currentHighlightedIndex]?.token.text
          : null,
    };
  }
}
